import React, { useRef, useEffect, useState } from 'react';
import Map, { Marker, Popup, NavigationControl, FullscreenControl, Source, Layer } from 'react-map-gl/mapbox';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = 'pk.eyJ1Ijoia2FsMjIiLCJhIjoiY21nOGd4aW5uMDdpYTJrb2NrcnM2eWh5NyJ9.PwMmJHuuDjwhLcFOVwQX5g';

const STATUS_COLORS = {
  'online': '#3ac37e',
  'offline': '#d72020'
};

// Device shape constants - SWAPPED: MFR Node is square, Band Dendrometer is diamond
const DEVICE_SHAPES = {
  'Sap Flow Sensor': 'circle',
  'MFR Node': 'square',
  'Band Dendrometer': 'diamond',
  'WXT536 Sensor': 'triangle',
  'AQT530 Sensor': 'hexagon'
};

// Base map styles
const BASE_MAP_STYLES = {
  'light': 'mapbox://styles/mapbox/light-v9',
  'satellite': 'mapbox://styles/mapbox/satellite-streets-v12',
  'outdoors': 'mapbox://styles/mapbox/outdoors-v12',
};

// Calculate dynamic status based on lastSeen timestamp
// Uses 1 hour (3600 seconds) as the threshold - matching the table logic
const calculateStatusFromLastSeen = (lastSeen) => {
  if (!lastSeen || lastSeen === '1970-01-01T00:00:00Z') {
    return 'offline';
  }
  
  const timestampUTC = lastSeen.endsWith('Z') ? lastSeen : lastSeen + 'Z';
  const lastSeenDate = new Date(timestampUTC);
  const currentTime = Date.now();
  const timeSinceLastSeen = currentTime - lastSeenDate.getTime();
  
  // 1 hour threshold (3600 * 1000 = 3600000 ms) - same as table
  if (timeSinceLastSeen <= 3600000) {
    return 'online';
  }
  return 'offline';
};

// Get color based on lastSeen timestamp
const getStatusColor = (lastSeen) => {
  const status = calculateStatusFromLastSeen(lastSeen);
  return STATUS_COLORS[status];
};

// Get icon based on lastSeen timestamp
const getStatusIcon = (lastSeen) => {
  const status = calculateStatusFromLastSeen(lastSeen);
  if (status === 'online') {
    return <CheckCircleRoundedIcon className="text-green-600" fontSize="small" />;
  }
  return <ErrorOutlineRoundedIcon className="text-red-600" fontSize="small" />;
};

// Helper function to format last seen date and create tooltip content
const getStatusTooltip = (lastSeen, timezone = 'UTC') => {
  if (!lastSeen || lastSeen === '1970-01-01T00:00:00Z') {
    return 'No activity recorded';
  }
  
  // Ensure timestamp is treated as UTC by appending Z if not present
  const timestampUTC = lastSeen.endsWith('Z') ? lastSeen : lastSeen + 'Z';
  const lastSeenDate = new Date(timestampUTC);
  const currentTime = Date.now();
  const timeSinceLastSeen = currentTime - lastSeenDate.getTime();
  
  // Format date
  let formattedLastSeenDate;
  if (timezone === 'UTC') {
    formattedLastSeenDate = lastSeenDate.toLocaleString(undefined, {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC'
    });
  } else {
    formattedLastSeenDate = lastSeenDate.toLocaleString(undefined, {
      timeZone: timezone,
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }
  
  // Check if overdue (more than 1 hour)
  const isOverdue = timeSinceLastSeen > 3600000;
  
  return `Last seen: ${formattedLastSeenDate}${isOverdue ? ' (overdue)' : ''}`;
};

// Function to render device shape
const renderDeviceMarker = (deviceType, color, size = 12) => {
  const shape = DEVICE_SHAPES[deviceType] || 'circle';
  
  if (shape === 'circle') {
    return (
      <div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          backgroundColor: color,
          border: '2px solid #fff',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          cursor: 'pointer'
        }}
      />
    );
  } else if (shape === 'diamond') {
    return (
      <div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          backgroundColor: color,
          border: '2px solid #fff',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          cursor: 'pointer',
          transform: 'rotate(45deg)'
        }}
      />
    );
  } else if (shape === 'square') {
    return (
      <div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          backgroundColor: color,
          border: '2px solid #fff',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          cursor: 'pointer'
        }}
      />
    );
  } else if (shape === 'triangle') {
    return (
      <svg width={size + 3} height={size + 3} viewBox="0 0 12 12" style={{ cursor: 'pointer', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>
        <polygon points="6,1 1,11 11,11" fill={color} stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    );
  } else if (shape === 'hexagon') {
    return (
      <svg width={size} height={size} viewBox="0 0 12 12" style={{ cursor: 'pointer', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>
        <polygon points="3,0.5 9,0.5 12,6 9,11.5 3,11.5 0,6" fill={color} stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    );
  }
};

// Layer style for location labels (main page only)
const labelLayerStyle = {
  'id': 'location-labels',
  'type': 'symbol',
  'source': 'locations',
  'layout': {
    'text-field': ['get', 'title'],
    'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
    'text-radial-offset': 0.5,
    'text-justify': 'auto',
    'text-font': [
      'Open Sans Semibold',
      'Arial Unicode MS Bold'
    ],
    'text-size': 11
  },
  'paint': {
    'text-color': '#444'
  }
};

export default function MapboxMap({ 
  data, 
  onMarkerClick, 
  height = 400, 
  showLegend = false, 
  defaultZoom = 13,
  showDeviceDetails = false 
}) {
  const mapRef = useRef(null);
  const [popupInfo, setPopupInfo] = useState(null);
  
  // Remember basemap selection in localStorage
  const [baseMapStyle, setBaseMapStyle] = useState(() => {
    const savedStyle = localStorage.getItem('crocus_basemap');
    return savedStyle || 'light';
  });
  
  // Get timezone from localStorage and listen for changes
  const [timezone, setTimezone] = useState(() => {
    return localStorage.getItem('crocus_timezone') || 'UTC';
  });
  
  useEffect(() => {
    const handleTimezoneChange = (e) => {
      setTimezone(e.detail);
    };
    
    window.addEventListener('timezoneChange', handleTimezoneChange);
    return () => window.removeEventListener('timezoneChange', handleTimezoneChange);
  }, []);
  
  const [viewState, setViewState] = useState({
    longitude: -87.6298,
    latitude: 41.8781,
    zoom: defaultZoom
  });

  // Save basemap selection when it changes
  useEffect(() => {
    localStorage.setItem('crocus_basemap', baseMapStyle);
  }, [baseMapStyle]);

  // Helper to fit map bounds to all data points
  const fitBoundsToData = () => {
    const locations = Array.isArray(data) ? data : [data];
    if (!locations.length || !locations[0].lat || !mapRef.current) return;

    if (locations.length > 1) {
      const lngs = locations.map(l => l.lon);
      const lats = locations.map(l => l.lat);
      const lngMin = Math.min(...lngs);
      const lngMax = Math.max(...lngs);
      const latMin = Math.min(...lats);
      const latMax = Math.max(...lats);

      mapRef.current.fitBounds(
        [[lngMin, latMin], [lngMax, latMax]],
        { padding: 50, maxZoom: defaultZoom + 2, duration: 1000 }
      );
    } else {
      setViewState({
        longitude: locations[0].lon,
        latitude: locations[0].lat,
        zoom: defaultZoom
      });
    }
  };

  // Fit bounds when data changes (covers data arriving after map is loaded)
  useEffect(() => {
    const locations = Array.isArray(data) ? data : [data];
    if (!locations.length || !locations[0].lat) return;

    if (locations.length > 1) {
      const lngs = locations.map(l => l.lon);
      const lats = locations.map(l => l.lat);
      const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;

      setViewState({
        longitude: centerLng,
        latitude: centerLat,
        zoom: defaultZoom
      });
    } else {
      setViewState({
        longitude: locations[0].lon,
        latitude: locations[0].lat,
        zoom: defaultZoom
      });
    }

    // Try fitBounds after a short delay for when map is already loaded
    setTimeout(fitBoundsToData, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, defaultZoom]);

  // Also fit bounds when the map first loads (covers map loading after data is ready)
  const handleMapLoad = () => {
    fitBoundsToData();
  };

  const handleCopyCoordinates = (lat, lon) => {
    navigator.clipboard.writeText(`${lat}, ${lon}`);
  };

  const locations = Array.isArray(data) ? data : [data];

  // Create GeoJSON for labels (only for main page)
  const geoJsonData = !showDeviceDetails ? {
    type: 'FeatureCollection',
    features: locations
      .filter(loc => loc.lat && loc.lon)
      .map(loc => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [loc.lon, loc.lat]
        },
        properties: {
          title: loc.locationName || loc.label || ''
        }
      }))
  } : null;

  // Group devices by coordinates to handle overlapping
  const deviceGroups = {};
  if (showDeviceDetails) {
    locations.forEach((loc, idx) => {
      const key = `${loc.lat.toFixed(6)},${loc.lon.toFixed(6)}`;
      if (!deviceGroups[key]) {
        deviceGroups[key] = [];
      }
      deviceGroups[key].push({ ...loc, originalIndex: idx });
    });
  }

  return (
    <div>
      <div style={{ width: '100%', height: `${height}px`, position: 'relative' }}>
        {/* Base Map Toggle */}
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 1,
          backgroundColor: 'white',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}>
          <ToggleButtonGroup
            value={baseMapStyle}
            exclusive
            onChange={(e, newStyle) => newStyle && setBaseMapStyle(newStyle)}
            size="small"
            aria-label="base map style"
          >
            <ToggleButton value="light" aria-label="light map" style={{ fontSize: '11px', padding: '4px 8px' }}>
              Light
            </ToggleButton>
            <ToggleButton value="satellite" aria-label="satellite map" style={{ fontSize: '11px', padding: '4px 8px' }}>
              Satellite
            </ToggleButton>
            <ToggleButton value="outdoors" aria-label="terrain map" style={{ fontSize: '11px', padding: '4px 8px' }}>
              Terrain
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        <Map
          ref={mapRef}
          {...viewState}
          onMove={evt => setViewState(evt.viewState)}
          onLoad={handleMapLoad}
          mapboxAccessToken={MAPBOX_TOKEN}
          mapStyle={BASE_MAP_STYLES[baseMapStyle]}
          style={{ width: '100%', height: '100%' }}
          onClick={() => setPopupInfo(null)}
        >
          <FullscreenControl position="top-right" />
          <NavigationControl position="bottom-right" showCompass={false} />

          {/* Add location labels for main page */}
          {!showDeviceDetails && geoJsonData && (
            <Source id="locations" type="geojson" data={geoJsonData}>
              <Layer {...labelLayerStyle} />
            </Source>
          )}

          {/* Render markers - handle overlapping devices */}
          {showDeviceDetails ? (
            Object.values(deviceGroups).map((group, groupIdx) => {
              const loc = group[0];
              const hasMultiple = group.length > 1;
              
              return (
                <Marker
                  key={groupIdx}
                  longitude={loc.lon}
                  latitude={loc.lat}
                  anchor="center"
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    setPopupInfo(group);
                    if (onMarkerClick && group.length === 1) onMarkerClick(loc);
                  }}
                >
                  {hasMultiple ? (
                    // Multiple devices at same location - render stacked
                    <div style={{ position: 'relative', width: '14px', height: '14px' }}>
                      {group.map((device, i) => {
                        const color = getStatusColor(device.lastSeen);
                        const offset = i * 3;
                        return (
                          <div 
                            key={i}
                            style={{
                              position: 'absolute',
                              top: `${offset}px`,
                              left: `${offset}px`,
                              zIndex: i
                            }}
                          >
                            {renderDeviceMarker(device.deviceType, color, 12)}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    renderDeviceMarker(loc.deviceType, getStatusColor(loc.lastSeen), 12)
                  )}
                </Marker>
              );
            })
          ) : (
            locations
              .filter(loc => loc.lat && loc.lon)
              .map((loc, idx) => {
                const color = getStatusColor(loc.lastSeen);
                
                return (
                  <Marker
                    key={idx}
                    longitude={loc.lon}
                    latitude={loc.lat}
                    anchor="center"
                    onClick={(e) => {
                      e.originalEvent.stopPropagation();
                      setPopupInfo([loc]);
                      if (onMarkerClick) onMarkerClick(loc);
                    }}
                  >
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: color,
                        border: '2px solid #fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        cursor: 'pointer'
                      }}
                    />
                  </Marker>
                );
              })
          )}

          {popupInfo && (
            <Popup
              longitude={Array.isArray(popupInfo) ? popupInfo[0].lon : popupInfo.lon}
              latitude={Array.isArray(popupInfo) ? popupInfo[0].lat : popupInfo.lat}
              onClose={() => setPopupInfo(null)}
              closeButton={true}
              closeOnClick={false}
              anchor="bottom"
              offset={15}
              maxWidth="300px"
            >
              <div style={{ padding: '8px', minWidth: '220px' }}>
                {/* Handle multiple devices at same location */}
                {Array.isArray(popupInfo) && popupInfo.length > 1 && showDeviceDetails ? (
                  <>
                    <h2 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600 }}>
                      Multiple Devices ({popupInfo.length})
                    </h2>
                    {popupInfo.map((device, i) => (
                      <div key={i} style={{ marginBottom: i < popupInfo.length - 1 ? '12px' : '0', paddingBottom: i < popupInfo.length - 1 ? '12px' : '0', borderBottom: i < popupInfo.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>{device.deviceName}</div>
                        <div style={{ display: 'flex', gap: '8px', fontSize: '11px', marginBottom: '4px' }}>
                          <div>
                            <strong>Type:</strong> {device.deviceType}
                          </div>
                          <div>
                            <strong>ID:</strong> {device.label}
                          </div>
                        </div>
                        {device.treeId && (
                          <div style={{ fontSize: '11px' }}>
                            <strong>Tree {device.treeId}:</strong> {device.treeSpecies} ({device.treeDiameter})
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  /* Single device popup */
                  <>
                    {!showDeviceDetails && (
                      <>
                        <h2 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600 }}>
                          {Array.isArray(popupInfo) ? popupInfo[0].locationName : popupInfo.locationName}
                          {(Array.isArray(popupInfo) ? popupInfo[0].link : popupInfo.link) && (
                            <a 
                              href={Array.isArray(popupInfo) ? popupInfo[0].link : popupInfo.link}
                              style={{ color: '#2563eb', textDecoration: 'none', marginLeft: '4px' }}
                            >
                              {Array.isArray(popupInfo) ? popupInfo[0].abbrev : popupInfo.abbrev}
                            </a>
                          )}
                        </h2>
                        
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <small style={{ color: '#6b7280', fontWeight: 600, fontSize: '0.7rem' }}>Status</small>
                            <Tooltip 
                              title={getStatusTooltip(
                                Array.isArray(popupInfo) ? popupInfo[0].lastSeen : popupInfo.lastSeen,
                                timezone
                              )} 
                              arrow
                              componentsProps={{ 
                                tooltip: { 
                                  sx: { background: '#000' } 
                                } 
                              }}
                            >
                              <span>
                                {getStatusIcon(Array.isArray(popupInfo) ? popupInfo[0].lastSeen : popupInfo.lastSeen)}
                              </span>
                            </Tooltip>
                          </div>
                          
                          {(Array.isArray(popupInfo) ? popupInfo[0].city : popupInfo.city) && (
                            <div>
                              <small style={{ color: '#6b7280', fontWeight: 600, fontSize: '0.7rem', display: 'block' }}>City</small>
                              <div style={{ fontSize: '0.8rem' }}>{Array.isArray(popupInfo) ? popupInfo[0].city : popupInfo.city}</div>
                            </div>
                          )}
                        </div>

                        {/* Device counts by type */}
                        {(Array.isArray(popupInfo) ? popupInfo[0].deviceCounts : popupInfo.deviceCounts) && (
                          <div style={{ marginBottom: '8px' }}>
                            <small style={{ color: '#6b7280', fontWeight: 600, fontSize: '0.7rem', display: 'block', marginBottom: '4px' }}>Devices</small>
                            <div style={{ fontSize: '0.75rem', lineHeight: '1.5' }}>
                              {(() => {
                                const counts = Array.isArray(popupInfo) ? popupInfo[0].deviceCounts : popupInfo.deviceCounts;
                                return (
                                  <>
                                    {counts.mfrNodes > 0 && <div>{counts.mfrNodes} MFR Node{counts.mfrNodes > 1 ? 's' : ''}</div>}
                                    {counts.sapFlowMeters > 0 && <div>{counts.sapFlowMeters} Sap Flow Meter{counts.sapFlowMeters > 1 ? 's' : ''}</div>}
                                    {counts.bandDendrometers > 0 && <div>{counts.bandDendrometers} Band Dendrometer{counts.bandDendrometers > 1 ? 's' : ''}</div>}
                                    {counts.wxt536Sensors > 0 && <div>{counts.wxt536Sensors} WXT536 Sensor{counts.wxt536Sensors > 1 ? 's' : ''}</div>}
                                    {counts.aqt530Sensors > 0 && <div>{counts.aqt530Sensors} AQT530 Sensor{counts.aqt530Sensors > 1 ? 's' : ''}</div>}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {showDeviceDetails && (
                      <>
                        <h2 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600 }}>
                          {Array.isArray(popupInfo) ? popupInfo[0].deviceName : popupInfo.deviceName}
                        </h2>
                        
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <small style={{ color: '#6b7280', fontWeight: 600, fontSize: '0.7rem' }}>Status</small>
                            <Tooltip 
                              title={getStatusTooltip(
                                Array.isArray(popupInfo) ? popupInfo[0].lastSeen : popupInfo.lastSeen,
                                timezone
                              )} 
                              arrow
                              componentsProps={{ 
                                tooltip: { 
                                  sx: { background: '#000' } 
                                } 
                              }}
                            >
                              <span>
                                {getStatusIcon(Array.isArray(popupInfo) ? popupInfo[0].lastSeen : popupInfo.lastSeen)}
                              </span>
                            </Tooltip>
                          </div>
                          
                          <div>
                            <small style={{ color: '#6b7280', fontWeight: 600, fontSize: '0.7rem', display: 'block' }}>Type</small>
                            <div style={{ fontSize: '0.8rem' }}>{Array.isArray(popupInfo) ? popupInfo[0].deviceType : popupInfo.deviceType}</div>
                          </div>
                          
                          <div>
                            <small style={{ color: '#6b7280', fontWeight: 600, fontSize: '0.7rem', display: 'block' }}>Serial</small>
                            <div style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{Array.isArray(popupInfo) ? popupInfo[0].label : popupInfo.label}</div>
                          </div>
                        </div>

                        <div style={{ marginBottom: '8px', fontSize: '0.75rem' }}>
                          {(Array.isArray(popupInfo) ? popupInfo[0].site : popupInfo.site) && (
                            <div style={{ marginBottom: '3px' }}>
                              <strong>Site:</strong> {Array.isArray(popupInfo) ? popupInfo[0].site : popupInfo.site}
                            </div>
                          )}
                          {(Array.isArray(popupInfo) ? popupInfo[0].treeId : popupInfo.treeId) && (
                            <div style={{ marginBottom: '3px' }}>
                              <strong>Tree ID:</strong> {Array.isArray(popupInfo) ? popupInfo[0].treeId : popupInfo.treeId}
                            </div>
                          )}
                          {(Array.isArray(popupInfo) ? popupInfo[0].treeSpecies : popupInfo.treeSpecies) && (
                            <div style={{ marginBottom: '3px' }}>
                              <strong>Species:</strong> {Array.isArray(popupInfo) ? popupInfo[0].treeSpecies : popupInfo.treeSpecies}
                            </div>
                          )}
                          {(Array.isArray(popupInfo) ? popupInfo[0].treeDiameter : popupInfo.treeDiameter) && (
                            <div style={{ marginBottom: '3px' }}>
                              <strong>DBH:</strong> {Array.isArray(popupInfo) ? popupInfo[0].treeDiameter : popupInfo.treeDiameter}
                            </div>
                          )}
                          {(Array.isArray(popupInfo) ? popupInfo[0].attachedSensors : popupInfo.attachedSensors) && (Array.isArray(popupInfo) ? popupInfo[0].attachedSensors.length : popupInfo.attachedSensors.length) > 0 && (
                            <div style={{ marginBottom: '3px' }}>
                              <strong>Sensors:</strong> {(Array.isArray(popupInfo) ? popupInfo[0].attachedSensors : popupInfo.attachedSensors).join(', ')}
                            </div>
                          )}
                        </div>

                        <div style={{ fontSize: '0.65rem', color: '#6b7280', marginBottom: '6px' }}>
                          {Array.isArray(popupInfo) ? popupInfo[0].locationName : popupInfo.locationName} ({Array.isArray(popupInfo) ? popupInfo[0].locationAbbrev : popupInfo.locationAbbrev})
                        </div>
                      </>
                    )}
                    
                    <div style={{ 
                      backgroundColor: '#f3f4f6', 
                      padding: '6px', 
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontFamily: 'monospace'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <RoomOutlinedIcon fontSize="small" style={{ color: '#2563eb', fontSize: '14px' }} />
                          <span>{(Array.isArray(popupInfo) ? popupInfo[0].lat : popupInfo.lat).toFixed(6)}, {(Array.isArray(popupInfo) ? popupInfo[0].lon : popupInfo.lon).toFixed(6)}</span>
                        </div>
                        <IconButton 
                          size="small" 
                          onClick={() => handleCopyCoordinates(Array.isArray(popupInfo) ? popupInfo[0].lat : popupInfo.lat, Array.isArray(popupInfo) ? popupInfo[0].lon : popupInfo.lon)}
                          aria-label="Copy coordinates"
                          style={{ padding: '2px' }}
                        >
                          <FileCopyOutlinedIcon fontSize="small" style={{ fontSize: '12px' }} />
                        </IconButton>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Popup>
          )}
        </Map>
      </div>
      
      {showLegend && (
        <div className="mt-3 py-2 px-4 bg-gray-50 rounded border border-gray-200">
          <div className="flex items-center justify-center gap-6">
            {/* --- Device Status --- */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-700">Device Status:</span>
              <Tooltip title="Online" arrow>
                <div
                  aria-label="Online"
                  role="img"
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: STATUS_COLORS.online,
                    border: '2px solid #fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    cursor: 'default',
                  }}
                />
              </Tooltip>
              <Tooltip title="Offline" arrow>
                <div
                  aria-label="Offline"
                  role="img"
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: STATUS_COLORS.offline,
                    border: '2px solid #fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    cursor: 'default',
                  }}
                />
              </Tooltip>
            </div>

            {/* Divider */}
            <div style={{ width: '1px', height: '18px', backgroundColor: '#e5e7eb' }} />

            {/* --- Device Symbols --- */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-700">Device Symbol:</span>
              <Tooltip title="Sap Flow" arrow>
                <div
                  aria-label="Sap Flow"
                  role="img"
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: '#888',
                    border: '2px solid #fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    cursor: 'default',
                  }}
                />
              </Tooltip>
              <Tooltip title="MFR Node" arrow>
                <div
                  aria-label="MFR Node"
                  role="img"
                  style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#888',
                    border: '2px solid #fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    cursor: 'default',
                  }}
                />
              </Tooltip>
              <Tooltip title="Band Dendrometer" arrow>
                <div
                  aria-label="Band Dendrometer"
                  role="img"
                  style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#888',
                    border: '2px solid #fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    transform: 'rotate(45deg)',
                    cursor: 'default',
                  }}
                />
              </Tooltip>
              <Tooltip title="WXT536 Weather" arrow>
                <svg
                  aria-label="WXT536 Weather"
                  role="img"
                  width="15"
                  height="15"
                  viewBox="0 0 12 12"
                  style={{ cursor: 'default', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.3))' }}
                >
                  <polygon points="6,1 1,11 11,11" fill="#888" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
                </svg>
              </Tooltip>
              <Tooltip title="AQT530 Air Quality" arrow>
                <svg
                  aria-label="AQT530 Air Quality"
                  role="img"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  style={{ cursor: 'default', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.3))' }}
                >
                  <polygon points="3,0.5 9,0.5 12,6 9,11.5 3,11.5 0,6" fill="#888" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
                </svg>
              </Tooltip>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}